import React, { forwardRef, Ref, useEffect, useRef } from 'react'
import InputLabel from './input-label'
import { ENTER_MSG } from '../../Configuration'
import { Capacitor } from '@capacitor/core'
const InputText = forwardRef((inputProps:any,ref:Ref<HTMLInputElement>) => {
  const internalInputRef = useRef<HTMLInputElement | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  const lastInputTimeRef = useRef(0)
  const lastKeyNavTimeRef = useRef(0)
  const lastFocusTimeRef = useRef(0)
  const isNative = Capacitor.isNativePlatform()
  const getInputEl = () => {
    if (internalInputRef.current) return internalInputRef.current
    if (!ref) return null
    if (typeof ref === "function") return null
    return ref.current
  }
  const assignInputRef = (element: HTMLInputElement | null) => {
    internalInputRef.current = element
    if (!ref) return
    if (typeof ref === 'function') {
      ref(element)
      return
    }
    ref.current = element
  }
  const openNativeDatePicker = () => {
    if (inputProps?.type !== 'date' || inputProps?.disabled || inputProps?.readonly) {
      return
    }

    const input = getInputEl()
    if (!input) return

    input.focus()
    if (typeof input.showPicker === 'function') {
      input.showPicker()
      return
    }

    input.click()
  }

  useEffect(() => {
    if (isNative) return

    const handleFocusIn = () => {
      lastFocusTimeRef.current = Date.now()
    }
    const handleInput = () => {
      lastInputTimeRef.current = Date.now()
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      const navKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']
      if (navKeys.includes(e.key)) {
        lastKeyNavTimeRef.current = Date.now()
      }
    }
    const handleScroll = (e: Event) => {
      const activeEl = document.activeElement as HTMLElement | null
      // Prevent blur for autosuggest input
      if (activeEl?.id === "AccountName") return
      const input = getInputEl()

      if (!input) return
      const now = Date.now()
      // must be focused
      if (document.activeElement !== input) return
      // do not blur if empty
      if (!input.value) return
      // do not blur while typing
      if (now - lastInputTimeRef.current < 200) return
      // do not blur during cursor navigation
      if (now - lastKeyNavTimeRef.current < 200) return
      // do not blur immediately after focus
      if (now - lastFocusTimeRef.current < 200) return
      input.blur()
    }

    const scrollEvent = isNative ? 'touchmove' : 'scroll'

    window.addEventListener(scrollEvent, handleScroll, true)
    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('input', handleInput, true)
    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      window.removeEventListener(scrollEvent, handleScroll, true)
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('input', handleInput, true)
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [])

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const activeEl = document.activeElement as HTMLElement | null;

  // Prevent blur for AccountName autosuggest input
  if (activeEl?.id === "AccountName") return;

  const input = getInputEl()

    const isNative = Capacitor.isNativePlatform()
    if (!isNative && input?.value === '') {
      if (cleanupRef.current) {
        cleanupRef.current()
      }

      const handleScroll = () => {
        if (Date.now() - lastFocusTimeRef.current < 200) return 
        const now = Date.now()
        if (now - lastInputTimeRef.current < 200) return
        if (now - lastKeyNavTimeRef.current < 200) return
        requestAnimationFrame(() => {
          input?.blur()
        })
      }
      window.addEventListener('scroll', handleScroll, true)
      cleanupRef.current = () => {
        window.removeEventListener('scroll', handleScroll, true)
        cleanupRef.current = null
      }
    }
  }

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
      }
    }
  }, [])

  return (
    <div className={inputProps?.formClassName}>
      {inputProps?.label && <InputLabel labelProps={{ htmlFor: inputProps?.inputProps?.id }} label={inputProps?.label} className={inputProps?.labelclassName} infoLabel={inputProps?.info} isinfoLabel={inputProps?.isInfo} isinfoIcon={inputProps?.infoIcon} isinfoClassName={inputProps?.infoClassName} currency={inputProps?.isCurrency} mandatory={inputProps?.isMandatory} position={inputProps?.position}/>}
      <div className={`p-relative d-flex flex-column ${inputProps?.type === 'date' && isNative? 'inputswithicon date-input-with-icon' : ''}`}>
      <input
        name={inputProps?.inputProps?.name}
        id={inputProps?.inputProps?.id}
        value={inputProps?.value}
        maxLength={inputProps?.inputProps?.maxLength}
        onChange={inputProps?.onChange}
        disabled={inputProps?.disabled}
        readOnly={inputProps?.readonly}
        className={`input ${inputProps?.className}`}
        style={inputProps?.style}
        aria-label={inputProps['aria-label'] || inputProps?.label}
        aria-required={inputProps?.isMandatory}
        size={inputProps?.inputProps?.size || 'sm'}
        type={inputProps?.type || 'text'}
        placeholder={inputProps?.inputProps?.placeholder}
        min={inputProps?.min}
        max={inputProps?.max}
        ref={assignInputRef}
        onFocus={inputProps?.onFocus}
        onBlur={inputProps?.onBlur} 
        autoComplete="on"  
        onClick={handleClick}    
        onKeyDown={inputProps?.onKeyDown}
      />
      {inputProps?.type === 'date' && isNative && (
        <button
          type="button"
          className="date-picker-trigger no-bg border-0 p-0"
          aria-label={`Open ${inputProps?.label || inputProps?.inputProps?.name || 'date'} picker`}
          onClick={openNativeDatePicker}
          tabIndex={-1}
        >
          <span className="icon-rb-date color-primary font-16"></span>
        </button>
      )}
      {inputProps.isInputIcon && <span className='icon-email-person'></span>}
      {(inputProps.error && inputProps?.errorMsg)
        ? <p className='errormsg text-capitalize'>{inputProps.errorMsg}</p>
        : inputProps.error
          ? <p className='errormsg text-capitalize'>{ENTER_MSG} {inputProps?.label || inputProps['aria-label']}</p>
          : ''}
          </div>
      </div>
  )
})
export default InputText
